import notification, { ArgsProps } from 'antd/es/notification/index';
import type { NotificationInstance } from 'antd/es/notification/interface';
import { XText } from 'CommonUI/components/XText/XText';
import React, { useMemo } from 'react';

const DTI = 'Notification';
const addDefaultParams = (args: ArgsProps) => {
  const draft = { ...args };

  if (!draft.props) {
    draft.props = {};
  } else {
    draft.props = { ...args.props };
  }

  // Add DTI
  if (!draft.props['data-testid']) {
    draft.props['data-testid'] = 'Notification';
  }

  // Add the type of notification
  if (!(draft.props as { 'data-testvalue'?: string })['data-testvalue']) {
    (draft.props as { 'data-testvalue'?: string })['data-testvalue'] = args.type || 'info';
  }

  if (!draft.style) {
    draft.style = {};
  } else {
    draft.style = { ...args.style };
  }

  // Add z-index
  if (draft.style.zIndex === 'undefined') {
    draft.style.zIndex = 'var(--z-index-notification)';
  }

  // Wrap title into DTI
  if (typeof draft.message === 'string') {
    draft.message = <span data-testid={`${DTI}__Message`}>{draft.message}</span>;
  }

  // Wrap description into DTI
  if (typeof draft.description === 'string') {
    draft.description = (
      <XText whiteSpacePreWrap data-testid={`${DTI}__Description`}>
        {draft.description}
      </XText>
    );
  }

  // Set default duration
  if (typeof draft.duration === 'undefined') {
    draft.duration = 3;
  }

  return draft;
};

const getNotification = <T extends NotificationInstance>(notification: T) => {
  const error = (args: Omit<ArgsProps, 'type'>) => notification.error(addDefaultParams({ ...args, type: 'error' }));
  const success = (args: Omit<ArgsProps, 'type'>) =>
    notification.success(addDefaultParams({ ...args, type: 'success' }));
  const info = (args: Omit<ArgsProps, 'type'>) => notification.info(addDefaultParams({ ...args, type: 'info' }));

  return {
    error,
    success,
    info,
    notification,
  };
};

export const useAntNotification = () => {
  return useMemo(() => getNotification(notification), []);
};

/** Use this hook when you need to render a ReactRouter link in the notification */
export const useAntNotificationWithContext = () => {
  const [api, contextHolder] = notification.useNotification();

  return [getNotification(api), contextHolder] as const;
};
